<script setup lang="ts">
import type { TMarketServiceConfig } from '~/src/types/markets'
import VideoModal from '~/components/video/VideoModal.vue'
import PdfViewerModal from '~/components/critical-insights/PdfViewerModal.vue'
import { logError } from '~/utils/log-error'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import { getServices } from '~/constants/services'
import { useGetVimeoData } from '~/composables/useGetVimeoData'
import useAuthentication from '~/composables/useAuthentication'
import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import Hero from '~/components/market-insights/Hero.vue'
import SectionTitle from '~/components/price-assessments/SectionTitle.vue'
import TheTabsBySlug from '~/components/common/TheTabsBySlug.vue'
import { COPPER } from '~/constants/services/constants'
import { MARKET_TYPE } from '~/src/constants/markets'
import { reformat } from '~/utils/date'
import { useReports } from '~/composables/useReports'
import { useModalStore } from '~/stores/modal'

const market: TMarketServiceConfig = COPPER
const baseUrl = `/${market.slug}/market-insights`
const title = `${market.name} Market Insights`

const isViewMoreActive = useState('isViewMoreActive', () => false)
const isPreviewVideo = ref(false)

const { $graphqlClient, $modal } = useNuxtApp()

const initialValues = getServices([market.slug, 'source'])
const { getReportAndModelData } = useReports()
const breadcrumbStore = useBreadcrumbStore()
const { items: breadcrumbMenu } = storeToRefs(breadcrumbStore)

const { getVimeoData, handlerPlay, vimeoSource, videoRatio, vimeoVideo } =
  useGetVimeoData()

const modalStore = useModalStore()
const { modalParams } = storeToRefs(modalStore)

const tabs = [
  { name: 'Analysis', slug: 'analysis', url: `${baseUrl}/analysis` },
  {
    name: 'Critical Insights',
    slug: 'critical-insights',
    url: `${baseUrl}/critical-insights`,
  },
  { name: 'Videos', slug: 'videos', url: `${baseUrl}/videos` },
]

const playVideo = async (vimeoData: any) => {
  const vimeoId = vimeoData.vimeoId || vimeoData.previewVimeoId
  isPreviewVideo.value = !vimeoData.vimeoId && !!vimeoData.previewVimeoId
  $modal.setInitialFields(initialValues)

  if (!vimeoId) {
    $modal.open('contact')
    return
  }

  await getVimeoData(vimeoId)
  handlerPlay()
}

const getCriticalInsights = async () => {
  const reports = await getReportAndModelData(
    market.slug,
    [MARKET_TYPE.CRITICAL_INSIGHTS],
    true,
    1,
  )
  return reports.map((report) => ({
    ...report,
    backgroundImage: report.images.desktop,
    publishedDate: reformat(report.date, 'dd MMM yyyy'),
  }))
}

const closeViewer = () => {
  modalStore.setModalParams({ pdfUrl: '' })
}

const { data, status, refresh } = useAsyncData(
  async () => {
    try {
      const [criticalInsights, marketInsights] = await Promise.all([
        getCriticalInsights(),
        $graphqlClient.getMarketInsightsArticles({
          market: market.slug,
        }),
      ])

      const fetchedArticles = marketInsights.articles
        .toSpliced(2, 0, ...criticalInsights)
        .slice(0, 5)

      return {
        articles: fetchedArticles,
        videos: marketInsights.videos,
        events: marketInsights.events,
      }
    } catch (error) {
      logError(error)
    }
  },
  { lazy: true },
)

const pdfUrl = computed(() => modalParams.value?.pdfUrl)
const isLoading = computed(() => status.value === 'pending')
const articles = computed(() => data.value?.articles)
const videos = computed(() => data.value?.videos)
const events = computed(() => data.value?.events)

useAuthentication(() => refresh())

definePageMeta({ layout: 'services' })
</script>

<template>
  <div>
    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 pt-8 md:items-start xl:pb-36 xl:pl-16 xl:pr-0 xl:pt-10"
    >
      <Breadcrumb :menu="breadcrumbMenu" />
      <div class="flex w-full flex-col gap-y-6">
        <SectionTitle :title="title" authorized :icon="market.iconColor" />
        <Hero
          v-if="!isViewMoreActive"
          :loading="isLoading"
          :market="market.slug"
          :articles="articles"
          :videos="videos"
          :events="events"
          @play-video="playVideo"
        />
        <TheTabsBySlug :tabs="tabs" />
        <div class="lg:min-h-[32rem]">
          <NuxtPage @play-video="playVideo" />
        </div>
      </div>
    </div>

    <VideoModal
      v-if="vimeoVideo && $modal.getModal('videoIntro')"
      :vimeo-source="vimeoSource"
      :video-ratio="videoRatio"
      :is-preview="isPreviewVideo"
    />
    <PdfViewerModal :url="pdfUrl" @close="closeViewer" />
  </div>
</template>
